import { cashRegisterApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetCashRegisterParamsI,
  ListCashRegisterI,
  OneCashRegisterForFrontI,
  OpenCashRegisterI,
  CloseCashRegisterI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getCashRegisters = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  dateFrom,
  dateTo,
}: GetCashRegisterParamsI): Promise<ServiceResponseI<DataI<ListCashRegisterI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId) params.append("branchId", branchId);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo)   params.append("dateTo",   dateTo);

    const { data } = await cashRegisterApi.get<ServiceResponseI<DataI<ListCashRegisterI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterById = async (
  id: string,
): Promise<ServiceResponseI<OneCashRegisterForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterApi.get<ServiceResponseI<OneCashRegisterForFrontI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterOpen = async (
  payload: OpenCashRegisterI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterApi.post<ServiceResponseI<string>>(
      "/open",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterClose = async (
  id:      string,
  payload: CloseCashRegisterI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterApi.patch<ServiceResponseI<string>>(
      `/close/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterAnnul = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useCashRegisters = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  dateFrom,
  dateTo,
}: GetCashRegisterParamsI) => {
  const cashRegistersQuery = useQuery(
    ["cash-registers", { pagination, sorting, globalFilter, branchId, dateFrom, dateTo }],
    () => getCashRegisters({ pagination, sorting, globalFilter, branchId, dateFrom, dateTo }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData:     true,
    },
  );

  return { cashRegistersQuery };
};